<template>
  <main class="guests-page">
    <div class="guests-page__head">
      <h1 class="guests-page__title">
        <template v-if="guest"
          >{{ guest.firstName }} {{ guest.lastName }}</template
        >
        <template v-else>Dettaglio ospite</template>
      </h1>
      <div style="display: flex; gap: 10px; flex-wrap: wrap">
        <router-link to="/guests" class="villa-btn villa-btn--ghost">
          <i class="fa-solid fa-arrow-left"></i> Archivio ospiti
        </router-link>
        <router-link
          v-if="guest && guest.facility"
          :to="`/facilities/${guest.facility.id}`"
          class="villa-btn villa-btn--ghost"
        >
          <i class="fa-solid fa-building"></i> Torna alla struttura
        </router-link>
        <button
          v-if="guest"
          class="villa-btn villa-btn--primary"
          :disabled="generatingContract"
          title="Genera il contratto dal template della struttura"
          @click="generateContract"
        >
          <i
            :class="
              generatingContract
                ? 'fa-solid fa-spinner fa-spin'
                : 'fa-solid fa-file-contract'
            "
          ></i>
          {{ generatingContract ? "Generazione…" : "Genera contratto" }}
        </button>
        <router-link
          v-if="guest"
          :to="{ name: 'edit-guest', params: { id: guest.id } }"
          class="villa-btn villa-btn--primary"
        >
          <i class="fa-solid fa-pen-to-square"></i> Modifica
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento…</div>

    <template v-else-if="guest">
      <!-- Navigazione interna alla scheda ospite (tabs, come pannello struttura) -->
      <div class="facility-tabs">
        <button
          v-for="t in tabs"
          :key="t.key"
          class="facility-tab"
          :class="{ 'facility-tab--active': activeTab === t.key }"
          @click="activeTab = t.key"
        >
          <i :class="t.icon"></i>
          <span>{{ t.label }}</span>
        </button>
      </div>

      <!-- ============ PANORAMICA ============ -->
      <section v-show="activeTab === 'panoramica'">
        <div class="guest-detail">
          <h3 class="detail-block-title">Panoramica</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Stato</span>
              <span class="detail-item__value">
                <span
                  :class="['status-badge', 'status-badge--' + guest.status]"
                >
                  {{ statusLabel(guest.status) }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Struttura</span>
              <span class="detail-item__value">
                <template v-if="guest.facility">{{
                  guest.facility.name
                }}</template>
                <span v-else class="guest-no-facility">Nessuna struttura</span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Data di ingresso</span>
              <span class="detail-item__value">{{
                formatDate(guest.admissionDate)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Data di dimissione</span>
              <span class="detail-item__value">{{
                formatDate(guest.dischargeDate)
              }}</span>
            </div>
            <div v-if="isAdminUser" class="detail-item">
              <span class="detail-item__label">Retta mensile</span>
              <span class="detail-item__value">{{
                feeLabel(guest.monthlyFee)
              }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ ANAGRAFICA ============ -->
      <section v-show="activeTab === 'anagrafica'">
        <!-- Dati anagrafici -->
        <div class="guest-detail">
          <h3 class="detail-block-title">Dati anagrafici</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Stato</span>
              <span class="detail-item__value">
                <span
                  :class="['status-badge', 'status-badge--' + guest.status]"
                >
                  {{ statusLabel(guest.status) }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Codice fiscale</span>
              <span class="detail-item__value">{{ val(guest.taxCode) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Sesso</span>
              <span class="detail-item__value">{{
                genderLabel(guest.gender)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Data di nascita</span>
              <span class="detail-item__value">{{
                formatDate(guest.birthDate)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Luogo di nascita</span>
              <span class="detail-item__value">
                {{ val(guest.birthPlace) }}
                <template v-if="guest.birthProvince"
                  >({{ guest.birthProvince }})</template
                >
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Telefono</span>
              <span class="detail-item__value">{{ val(guest.phone) }}</span>
            </div>
          </div>
        </div>

        <!-- Residenza -->
        <div class="guest-detail">
          <h3 class="detail-block-title">Residenza</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Indirizzo</span>
              <span class="detail-item__value">{{ val(guest.address) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Città</span>
              <span class="detail-item__value">{{ val(guest.city) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">CAP</span>
              <span class="detail-item__value">{{ val(guest.zip) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Provincia residenza</span>
              <span class="detail-item__value">{{ val(guest.province) }}</span>
            </div>
          </div>
        </div>

        <!-- Documento d'identità -->
        <div class="guest-detail">
          <h3 class="detail-block-title">Documento d'identità</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Tipo documento</span>
              <span class="detail-item__value">{{
                val(guest.identityDocumentType)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Numero documento</span>
              <span class="detail-item__value">{{
                val(guest.identityDocumentNumber)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Rilasciato da</span>
              <span class="detail-item__value">{{
                val(guest.identityDocumentIssuedBy)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Data rilascio</span>
              <span class="detail-item__value">{{
                formatDate(guest.identityDocumentIssueDate)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Data scadenza</span>
              <span class="detail-item__value">{{
                formatDate(guest.identityDocumentExpiryDate)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Responsabile / coobbligato -->
        <div class="guest-detail">
          <h3 class="detail-block-title">Responsabile / coobbligato</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Nome e cognome</span>
              <span class="detail-item__value">
                {{
                  fullName(
                    guest.responsibleFirstName,
                    guest.responsibleLastName
                  )
                }}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Relazione</span>
              <span class="detail-item__value">{{
                val(guest.responsibleRelationship)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Telefono</span>
              <span class="detail-item__value">{{
                val(guest.responsiblePhone)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Email</span>
              <span class="detail-item__value">{{
                val(guest.responsibleEmail)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Codice fiscale</span>
              <span class="detail-item__value">{{
                val(guest.responsibleTaxCode)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Residenza</span>
              <span class="detail-item__value">{{
                val(guest.responsibleResidenceAddress)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Città residenza</span>
              <span class="detail-item__value">
                {{ val(guest.responsibleResidenceCity) }}
                <template v-if="guest.responsibleResidenceProvince">
                  ({{ guest.responsibleResidenceProvince }})</template
                >
                <template v-if="guest.responsibleResidenceZip">
                  · {{ guest.responsibleResidenceZip }}</template
                >
              </span>
            </div>
          </div>
        </div>

        <!-- Ingresso e dati economici -->
        <div class="guest-detail">
          <h3 class="detail-block-title">Ingresso e dati economici</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Struttura</span>
              <span class="detail-item__value">
                <template v-if="guest.facility">{{
                  guest.facility.name
                }}</template>
                <span v-else class="guest-no-facility">Nessuna struttura</span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Data di ingresso</span>
              <span class="detail-item__value">{{
                formatDate(guest.admissionDate)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Data di dimissione</span>
              <span class="detail-item__value">{{
                formatDate(guest.dischargeDate)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Retta mensile</span>
              <span class="detail-item__value">{{
                feeLabel(guest.monthlyFee)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Contatto familiare / Medico di base / Note -->
        <div class="guest-detail">
          <h3 class="detail-block-title">Altri contatti</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-item__label">Contatto familiare</span>
              <span class="detail-item__value">
                {{ val(guest.familyContactName) }}
                <template v-if="guest.familyContactRelationship">
                  ({{ guest.familyContactRelationship }})
                </template>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Telefono familiare</span>
              <span class="detail-item__value">{{
                val(guest.familyContactPhone)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Email familiare</span>
              <span class="detail-item__value">{{
                val(guest.familyContactEmail)
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item__label">Medico di base</span>
              <span class="detail-item__value">
                {{ val(guest.generalPractitionerName) }}
                <template v-if="guest.generalPractitionerPhone">
                  · {{ guest.generalPractitionerPhone }}
                </template>
              </span>
            </div>
            <div class="detail-item" style="grid-column: 1 / -1">
              <span class="detail-item__label">Note</span>
              <span class="detail-item__value">{{ val(guest.notes) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ SANITARIO ============ -->
      <section v-show="activeTab === 'sanitario'">
        <!-- Farmaci personali / carichi stock ospite (Task D3B-refactor) -->
        <GuestMedicationItemsPanel :guest-id="guest.id" />

        <!-- Schede terapia strutturate (Task D4) -->
        <GuestTherapyPlanPanel
          :guest-id="guest.id"
          @documents-changed="refreshDocuments"
        />

        <!-- Stock farmaci personali: giacenza + movimenti (D14) -->
        <GuestMedicationStockPanel v-if="isCareUser" :guest-id="guest.id" />
      </section>

      <!-- ============ ASSISTENZA ============ -->
      <section v-show="activeTab === 'assistenza'">
        <!-- Diario assistenziale (D12) — area sanitaria: dev/admin/manager/infermiere -->
        <GuestCareActivitiesPanel v-if="isCareUser" :guest-id="guest.id" />
      </section>

      <!-- ============ PRESIDI IGIENICO-SANITARI ============ -->
      <section v-show="activeTab === 'presidi'">
        <!-- Forniture / magazzino ospite (D12B): consultazione dev/admin/manager/
             infermiere; la gestione stock interna è ristretta nel pannello. -->
        <GuestSuppliesPanel v-if="canViewSupplies" :guest-id="guest.id" />
      </section>

      <!-- ============ DOCUMENTI ============ -->
      <section v-show="activeTab === 'documenti'">
        <!-- Documento retta mensile (D10B) — funzione economica: dev/admin/manager
             (esclude infermiere). -->
        <div v-if="canManageEconomic" class="guest-detail billing-bar">
          <div class="billing-bar__info">
            <h3 class="detail-block-title" style="margin: 0 0 4px">
              Documento retta mensile
            </h3>
            <span class="guests-page__subtitle">
              Genera la ricevuta/proforma della retta per il mese selezionato.
            </span>
          </div>
          <div class="billing-bar__controls">
            <select v-model.number="billingMonth" class="search-box">
              <option v-for="(m, i) in monthNames" :key="i" :value="i + 1">
                {{ m }}
              </option>
            </select>
            <select v-model.number="billingYear" class="search-box">
              <option v-for="y in yearOptions" :key="y" :value="y">
                {{ y }}
              </option>
            </select>
            <select
              v-if="isAdminUser && bankAccounts.length"
              v-model.number="selectedBankAccountId"
              class="search-box"
              title="Conto bancario"
            >
              <option v-for="a in bankAccounts" :key="a.id" :value="a.id">
                {{ a.label }}{{ a.isDefault ? " (default)" : "" }}
              </option>
            </select>
            <button
              class="villa-btn villa-btn--primary"
              :disabled="generatingBilling"
              @click="generateBilling"
            >
              <i
                :class="
                  generatingBilling
                    ? 'fa-solid fa-spinner fa-spin'
                    : 'fa-solid fa-file-invoice'
                "
              ></i>
              {{
                generatingBilling ? "Generazione…" : "Genera documento retta"
              }}
            </button>
          </div>
        </div>

        <!-- Cartella documentale ospite (Task D2) -->
        <GuestDocumentsPanel
          :guest-id="guest.id"
          :refresh-key="documentsRefreshKey"
        />
      </section>
    </template>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GuestDocumentsPanel from "@/components/GuestDocumentsPanel.vue";
import GuestTherapyPlanPanel from "@/components/GuestTherapyPlanPanel.vue";
import GuestMedicationItemsPanel from "@/components/GuestMedicationItemsPanel.vue";
import GuestCareActivitiesPanel from "@/components/GuestCareActivitiesPanel.vue";
import GuestSuppliesPanel from "@/components/GuestSuppliesPanel.vue";
import GuestMedicationStockPanel from "@/components/GuestMedicationStockPanel.vue";

const MONTH_NAMES = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

export default {
  name: "GuestDetailPage",
  components: {
    GuestMedicationItemsPanel,
    GuestTherapyPlanPanel,
    GuestDocumentsPanel,
    GuestCareActivitiesPanel,
    GuestSuppliesPanel,
    GuestMedicationStockPanel,
  },
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    const now = new Date();
    return {
      guest: null,
      loading: true,
      activeTab: "panoramica",
      documentsRefreshKey: 0,
      generatingContract: false,
      generatingBilling: false,
      monthNames: MONTH_NAMES,
      billingMonth: now.getMonth() + 1,
      billingYear: now.getFullYear(),
      bankAccounts: [],
      selectedBankAccountId: null,
    };
  },
  computed: {
    ...mapGetters([
      "isAdminUser",
      "isCareUser",
      "canViewSupplies",
      "canManageEconomic",
    ]),
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 1, y, y + 1];
    },
    // Tabs della scheda ospite (alcune solo per dev/admin/manager).
    tabs() {
      const list = [
        {
          key: "panoramica",
          label: "Panoramica",
          icon: "fa-solid fa-gauge-high",
        },
        { key: "anagrafica", label: "Anagrafica", icon: "fa-solid fa-id-card" },
        { key: "sanitario", label: "Sanitario", icon: "fa-solid fa-pills" },
      ];
      if (this.isCareUser) {
        list.push({
          key: "assistenza",
          label: "Assistenza",
          icon: "fa-solid fa-hand-holding-medical",
        });
      }
      if (this.canViewSupplies) {
        list.push({
          key: "presidi",
          label: "Presidi igienico-sanitari",
          icon: "fa-solid fa-box-open",
        });
      }
      list.push({
        key: "documenti",
        label: "Documenti",
        icon: "fa-solid fa-folder-open",
      });
      return list;
    },
  },
  methods: {
    ...mapActions([
      "fetchGuestById",
      "generateGuestContract",
      "generateGuestBillingDocument",
      "fetchBankAccounts",
    ]),
    // Carica i conti attivi per la scelta in generazione (solo dev/admin; per il
    // manager l'endpoint è 403 e il backend userà il conto di default).
    async loadBankAccounts() {
      if (!this.isAdminUser) return;
      try {
        this.bankAccounts = await this.fetchBankAccounts({ activeOnly: true });
        const def =
          this.bankAccounts.find((a) => a.isDefault) || this.bankAccounts[0];
        this.selectedBankAccountId = def ? def.id : null;
      } catch {
        this.bankAccounts = [];
      }
    },
    // Genera il documento retta del mese/anno selezionati (D10B).
    async generateBilling() {
      if (this.generatingBilling) return;
      this.generatingBilling = true;
      try {
        await this.generateGuestBillingDocument({
          guestId: this.guest.id,
          month: this.billingMonth,
          year: this.billingYear,
          bankAccountId: this.selectedBankAccountId || undefined,
        });
        this.$toast.success(
          "Documento retta generato e salvato nella cartella documentale."
        );
        this.refreshDocuments();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Generazione del documento retta non riuscita."
        );
      } finally {
        this.generatingBilling = false;
      }
    },
    // Forza la Cartella documentale a ricaricare dopo la generazione di un PDF.
    refreshDocuments() {
      this.documentsRefreshKey += 1;
    },
    genderLabel(g) {
      if (g === "M") return "Maschio";
      if (g === "F") return "Femmina";
      return "Non indicato";
    },
    // Genera il contratto dal template DOCX attivo della struttura e aggiorna
    // la Cartella documentale dell'ospite. Errori mostrati con toast leggibile.
    async generateContract() {
      if (this.generatingContract) return;
      this.generatingContract = true;
      try {
        await this.generateGuestContract(this.guest.id);
        this.$toast.success(
          "Contratto generato e salvato nella cartella documentale."
        );
        this.refreshDocuments();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Generazione del contratto non riuscita."
        );
      } finally {
        this.generatingContract = false;
      }
    },
    statusLabel(status) {
      return (
        {
          active: "Attivo",
          discharged: "Dimesso",
          suspended: "Sospeso",
          deceased: "Deceduto",
        }[status] || status
      );
    },
    formatDate(d) {
      if (!d) return "—";
      const [y, m, day] = String(d).slice(0, 10).split("-");
      return day && m && y ? `${day}/${m}/${y}` : d;
    },
    feeLabel(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} / mese`;
    },
    // Stato vuoto leggibile per i campi anagrafici (D7).
    val(v) {
      return v === null || v === undefined || v === "" ? "Non indicato" : v;
    },
    fullName(first, last) {
      const s = `${first || ""} ${last || ""}`.trim();
      return s || "Non indicato";
    },
    async load() {
      this.loading = true;
      try {
        this.guest = await this.fetchGuestById(this.id);
      } catch (err) {
        this.$toast.error(err?.response?.data?.error || "Ospite non trovato.");
        this.$router.push("/guests");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.load();
    this.loadBankAccounts();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/AppLayout.css" />
